import { FeatureFlagContext, Features } from "@app/feature-flags";
import { DragAndDropFileUploader, PageSection } from "@app/shared";
import { wafflesKeyColumnName } from "@app/shared/waffles";
import { RoleRestricted, Roles } from "@app/user";
import { JSX, useContext, useEffect, useRef, useState } from "react";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { QESAlert, QESButton, QESCheckbox, QESRadioButton, QESRadioGroup } from "@app/shared/qes-standards";
import { ITermRefinementOption, TerminationsWizardStepProps } from "@app/terminations/import/interfaces";
import { TerminationsWizardContext } from "@app/terminations/import/TerminationsWizardContext";

const TerminationsWizardImportList = (props: TerminationsWizardStepProps): JSX.Element => {
  const wizardContext = useContext(TerminationsWizardContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const [termType, setTermType] = useState<string>("npi");
  const [termRefinementOptions, setTermRefinementOptions] = useState<ITermRefinementOption>({ infoMessage: <></>, isAllLocations: true, isAllSpecialties: true });
  const helpFileUri = useRef<string>(`${window.apiEndpointV2_0}/providerterminations/bulkterminationsamplefile/csv`).current;

  const uploadObjectKey = "bulkTerminationFile";
  const handleFileUpload = async (data: FormData) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const terminateByNpi = termType === "npi" || termType === "npi-tin";
        const terminateByTin = termType === "tin" || termType === "npi-tin";
        const response = await wizardContext.providerTerminationService.importBulkTerminationFile(
          data,
          terminateByNpi,
          terminateByTin,
          termRefinementOptions.isAllLocations,
          termRefinementOptions.isAllSpecialties
        );

        const validPlural = response.validRecordsCount !== 1;
        const nppesPlural = response.recordsInvalidByNppesCount !== 1;
        const validItems: JSX.Element = (
          <>
            <><span>{response.validRecordsCount} Valid Record{validPlural ? "s" : ""}</span><br /></>
            {response.recordsInvalidByNppesCount === 0 && <><span>All Valid NPIs are Valid NPPES NPIs</span><br /></>}
            {response.recordsInvalidByNppesCount !== 0 && <><span>{response.recordsInvalidByNppesCount} of your NPI{nppesPlural ? "s are not found in" : " is not found in"} NPPES</span><br /></>}
          </>
        )

        let invalidNpis: JSX.Element = null;
        let invalidTins: JSX.Element = null;

        if (response.invalidFormatNpis.length > 0 || response.invalidNotFoundNpis.length > 0) {
          invalidNpis = (
            <>
            {
              response.invalidFormatNpis.length > 0 &&
              <>
                <span> The following NPIs are formatted incorrectly:<br/><br/>
                  {
                      response.invalidFormatNpis.map((npi, index) => <><span key={index}>{npi}</span><br/></>)
                  }
                </span><br/>
              </>
            }
            {
              response.invalidNotFoundNpis.length > 0 &&
              <>
                <span> The following NPI(s) are correctly formatted but invalid because they do not exist in the official registry. Please verify their accuracy or consult the source of the data.<br/><br/>
                  {
                      response.invalidNotFoundNpis.map((npi, index) => <><span key={index}>{npi}</span><br/></>)
                  }
                </span>
              </>
            }
            </>
          );
        }

        if (response.invalidFormatTins.length > 0) {
          invalidTins = (
            <>
              <span> The following TINs are formatted incorrectly:<br/><br/>
                {
                  response.invalidFormatTins.map((tin, index) => <><span key={index}>{tin}</span><br /></>)
                }
              </span>
            </>
          );
        }

        wizardContext.setBulkProviderTerminationId(response.bulkProviderTerminationId);
        wizardContext.setInvalidNpis([{ invalidCount: response.invalidFormatNpis.length + response.invalidNotFoundNpis.length, invalidNpis: invalidNpis ?? <>All NPIs are valid</>, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setInvalidTins([{ invalidCount: response.invalidFormatTins.length, invalidTins: invalidTins ?? <>All TINs are valid</>, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setResultsSummary([{ validItems, [wafflesKeyColumnName]: "key" }]);
        wizardContext.setBulkImportFormData(data);
        wizardContext.setTerminateByNpi(terminateByNpi);
        wizardContext.setTerminateByTin(terminateByTin);
        wizardContext.setTerminateByAllLocations(termRefinementOptions.isAllLocations);
        wizardContext.setTerminateByAllSpecialties(termRefinementOptions.isAllSpecialties);
        resolve();
      } catch (error) {
        reject(error.responseJSON);
      }
    });
  };

  useEffect(() => {
    props.formIsValid(!!wizardContext.bulkImportFormData && !!termType && !!wizardContext.bulkProviderTerminationId);
  }, [wizardContext.bulkImportFormData, termType, wizardContext.bulkProviderTerminationId]);

  useEffect(() => {
    if (!termRefinementOptions.isAllLocations && !termRefinementOptions.isAllSpecialties) {
      setTermRefinementOptions({
        ...termRefinementOptions,
        infoMessage: 
          <>
            <div>When terminating providers:</div>
            <div>
              <ul>
                <li>Ensure&nbsp;<strong>NPI</strong>&nbsp;and/or&nbsp;<strong>TIN</strong>&nbsp;are selected as they are required identifiers.</li>
                <li>Specialty codes and address column information are required to terminate by specialty and location.&nbsp;</li>
                <ul>
                  <li>Address and Specialty fields are not required when terminating by All Locations or All Specialties, respectively.</li>
                </ul>
              </ul>
            </div>
          </>
      });
    } else if (!termRefinementOptions.isAllLocations) {
      setTermRefinementOptions({
        ...termRefinementOptions,
        infoMessage: <span>Address column information is required to terminate at specific locations.</span>
      });
    } else {
      setTermRefinementOptions({
        ...termRefinementOptions,
        infoMessage: <span>Specialty codes are required to terminate by specific specialties.</span>
      });
    }
  }, [termRefinementOptions.isAllLocations, termRefinementOptions.isAllSpecialties]);

  const downloadSampleFile = () => {
    authenticatedDownload(helpFileUri, undefined);
  }

  return (
    <PageSection>
      <div className="container">
        <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
          <fieldset>
            <div className="row">
              <div className="col">
                <p>Define what you wish to terminate on:</p>
                <QESRadioGroup
                  renderInline={true}
                >
                  <QESRadioButton
                    label="NPIs"
                    value="npi"
                    name="termination-type"
                    checked={termType === "npi"}
                    onChange={(e) => e.target.checked && setTermType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__termination-type--npi"
                  />
                  <QESRadioButton
                    label="TINs"
                    value="tin"
                    name="termination-type"
                    checked={termType === "tin"}
                    onChange={(e) => e.target.checked && setTermType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__termination-type--tin"
                  />
                  <QESRadioButton
                    label="Both NPIs and TINs"
                    value="npi-tin"
                    name="termination-type"
                    checked={termType === "npi-tin"}
                    onChange={(e) => e.target.checked && setTermType((e.target as HTMLInputElement).value)}
                    testId="import-terminations__termination-type--npi-tin"
                  />
                </QESRadioGroup>
              </div>
              {featureFlags.has(Features.ENHANCED_TERMINATION) &&
                <div className="col">
                  <p>Additional Termination Refinement Options:</p>
                  <div className="mt-n2">
                  <QESRadioGroup
                    renderInline={true}
                  >
                    <QESCheckbox
                      containerClassName="form-check mr-5"
                      label="All Locations"
                      checked={termRefinementOptions.isAllLocations}
                      onChange={(e) => setTermRefinementOptions({ ...termRefinementOptions, isAllLocations: e.target.checked })}
                      className="form-check-input mt-0"
                      testId="import-terminations__termination-refinement-type--all-locations"
                    />
                    <QESCheckbox
                      containerClassName="form-check"
                      label="All Specialties"
                      checked={termRefinementOptions.isAllSpecialties}
                      onChange={(e) => setTermRefinementOptions({ ...termRefinementOptions, isAllSpecialties: e.target.checked })}
                      className="form-check-input mt-0"
                      testId="import-terminations__termination-refinement-type--all-specialties" />
                  </QESRadioGroup>
                  </div>
                </div>}
            </div>
          </fieldset>
        </RoleRestricted>
        {featureFlags.has(Features.ENHANCED_TERMINATION) && (!termRefinementOptions.isAllLocations || !termRefinementOptions.isAllSpecialties) && <QESAlert type="info" classNames="mb-2" persist={true}>{termRefinementOptions.infoMessage}</QESAlert>}
        <p className="mt-3">Select the files you want to upload. The data needs to be in a <code>.csv</code> file. Or upload a file with the <QESButton qesType="secondary" className="bg-transparent font-weight-normal p-0" qesTestId="import-terminations__sample-file" onClick={downloadSampleFile}>correct format</QESButton>.</p>
        <DragAndDropFileUploader apiKeyName={uploadObjectKey} onUpload={handleFileUpload} filetype=".csv" hasBeenSuccessful={!!wizardContext.bulkImportFormData} testId="import-terminations__file-input" />
      </div>
    </PageSection>
  );
};

export default TerminationsWizardImportList;
